import { useMemo, useState } from 'react';
import ClassNames from '@streamloots/classnames';
import { PageFeed } from 'model/indexTS';
import trackEventManager from 'utils/event-manager';
import { TranslateInterface } from 'utils/translation';
import Dialog, { CleanDialogContent } from 'ui-library/dialog';
import { FixedCloseButton } from 'ui-library/fixed-close-button';
import { ImageSlider } from 'components/image-slider';
import theme from './feed-list.scss';

const classNames = ClassNames(theme);

export interface FeedAttachmentsImageProps extends TranslateInterface {
  feed: PageFeed;
}

export const FeedAttachmentsImage = ({ feed, t }: FeedAttachmentsImageProps): JSX.Element | null => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const attachmentsUrls = useMemo(() => feed.attachments.map(attachment => attachment.url), [feed.attachments]);

  const handleClick = (index: number) => {
    trackEventManager.feedClicked({
      button: 'expand image',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
    setIsOpen(true);
    setModalIndex(index);
  };

  const handleSlideRight = () => {
    trackEventManager.feedClicked({
      button: 'show next image',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
  };

  const handleSlideLeft = () => {
    trackEventManager.feedClicked({
      button: 'show previous image',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
  };

  const onPaginationClick = () => {
    trackEventManager.feedClicked({
      button: 'image carousel navigation',
      location: 'feed content',
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
      comment_count: feed.summary.commentCount,
    });
  };

  const onClose = () => {
    setIsOpen(false);
  };

  if (feed.attachments.length <= 0) {
    return null;
  }

  return (
    <>
      <ImageSlider
        images={attachmentsUrls}
        onImageClick={handleClick}
        slideLeftAria={t('pageFeed:feedDetail.previousAttachment')}
        slideRightAria={t('pageFeed:feedDetail.nextAttachment')}
        onSlideRightClick={handleSlideRight}
        onSlideLeftClick={handleSlideLeft}
        onPaginationClick={onPaginationClick}
      />
      <Dialog
        type="fullscreen"
        active={isOpen}
        onClose={onClose}
        onEscKeyDown={onClose}
        preventClose
        showBlurBackground
        renderDialogBody={() => {
          return (
            <CleanDialogContent>
              <FixedCloseButton onClose={onClose} ariaLabel={t('pageFeed:feedDetail.close')} />
              <div className={classNames('feed-detail__attachement__dialog')} onClick={onClose}>
                <ImageSlider
                  images={attachmentsUrls}
                  initialIndex={modalIndex}
                  imageClassName={classNames('feed-detail__attachement__dialog__img')}
                  className={classNames('feed-detail__attachement__dialog__slider')}
                  slideLeftAria={t('pageFeed:feedDetail.previousAttachment')}
                  slideRightAria={t('pageFeed:feedDetail.nextAttachment')}
                  onSlideRightClick={handleSlideRight}
                  onSlideLeftClick={handleSlideLeft}
                  hidePagination
                />
              </div>
            </CleanDialogContent>
          );
        }}
      />
    </>
  );
};
